import React, { useState } from 'react';
import { useQuery } from 'react-query';
import InsightsTab from '../components/InsightsTab';
import { FilterBar } from '../components/filters';
import { salesCyclesApi, insightsApi } from '../services/api';


const Insights: React.FC = () => {
  // State for filters
  const [selectedCycleTypes, setSelectedCycleTypes] = useState<string[]>(() => {
    // Initialize from localStorage if available, otherwise default to ["NEWBUSINESS"]
    const saved = localStorage.getItem('insights-cycle-type-filter');
    if (!saved) return ['NEWBUSINESS'];

    try {
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : ['NEWBUSINESS'];
    } catch {
      // If parsing fails, it's likely the old single-string format
      return saved ? [saved] : ['NEWBUSINESS'];
    }
  });

  const [selectedAccountValues, setSelectedAccountValues] = useState<string[]>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('insights-account-value-filter');
    if (!saved) return [];

    try {
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      localStorage.removeItem('insights-account-value-filter');
      return [];
    }
  });

  const [selectedMeetingDispositions, setSelectedMeetingDispositions] = useState<string[]>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem('insights-meeting-disposition-filter');
    if (!saved) {
      // Default: all dispositions except BAD_FIT and TRASH, including NOT_ANALYZED (Conflict removed)
      return ['TRANSACTIONAL', 'ENTERPRISE', 'NURTURE', 'NOT_ANALYZED'];
    }

    try {
      const parsed = JSON.parse(saved);
      // Filter out CONFLICT from any saved values (legacy cleanup)
      const filtered = Array.isArray(parsed) ? parsed.filter((v: string) => v !== 'CONFLICT') : ['TRANSACTIONAL', 'ENTERPRISE', 'NURTURE', 'NOT_ANALYZED'];
      return filtered.length > 0 ? filtered : ['TRANSACTIONAL', 'ENTERPRISE', 'NURTURE', 'NOT_ANALYZED'];
    } catch {
      return ['TRANSACTIONAL', 'ENTERPRISE', 'NURTURE', 'NOT_ANALYZED'];
    }
  });

  const [selectedSalesRep, setSelectedSalesRep] = useState<string | null>(() => {
    const saved = localStorage.getItem('insights-sales-rep-filter');
    return saved || null;
  });

  const [selectedTeam, setSelectedTeam] = useState<string | null>(() => {
    const saved = localStorage.getItem('insights-team-filter');
    return saved || null;
  });

  // Data fetching for insights (similar to dashboard pattern)
  const accountValue = selectedAccountValues && selectedAccountValues.length > 0 ? selectedAccountValues.join(',') : undefined;
  const cycleTypesString = selectedCycleTypes && selectedCycleTypes.length > 0 ? selectedCycleTypes.join(',') : undefined;

  const { data: tacticalData, isLoading: isLoadingTactical, error: tacticalError, isFetching: isFetchingTactical } = useQuery(
    ['tacticalInsights', cycleTypesString || 'all', accountValue, selectedSalesRep, selectedTeam],
    () => insightsApi.getTacticalInsights(cycleTypesString, accountValue, selectedSalesRep, selectedTeam),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes for fresher data
      cacheTime: 5 * 60 * 1000, // 5 minutes cache
      refetchOnWindowFocus: false,
      retry: 1,
      keepPreviousData: true,
    }
  );

  const { data: strategicData, isLoading: isLoadingStrategic, error: strategicError, isFetching: isFetchingStrategic } = useQuery(
    ['strategicInsights', cycleTypesString || 'all', accountValue, selectedSalesRep, selectedTeam],
    () => insightsApi.getStrategicInsights(cycleTypesString, accountValue, selectedSalesRep, selectedTeam),
    {
      staleTime: 2 * 60 * 1000, // 2 minutes for fresher data
      cacheTime: 5 * 60 * 1000, // 5 minutes cache
      refetchOnWindowFocus: false,
      retry: 1,
      keepPreviousData: true,
    }
  );

  const isLoading = isLoadingTactical || isLoadingStrategic;
  const isFetching = isFetchingTactical || isFetchingStrategic;
  const hasError = !!(tacticalError || strategicError);

  const handleCycleTypesChange = (cycleTypes: string[]) => {
    setSelectedCycleTypes(cycleTypes);
    if (cycleTypes.length > 0) {
      localStorage.setItem('insights-cycle-type-filter', JSON.stringify(cycleTypes));
    } else {
      localStorage.removeItem('insights-cycle-type-filter');
    }
  };

  const handleAccountValuesChange = (accountValues: string[]) => {
    setSelectedAccountValues(accountValues);
    if (accountValues.length > 0) {
      localStorage.setItem('insights-account-value-filter', JSON.stringify(accountValues));
    } else {
      localStorage.removeItem('insights-account-value-filter');
    }
  };

  const handleMeetingDispositionsChange = (dispositions: string[]) => {
    setSelectedMeetingDispositions(dispositions);
    if (dispositions.length > 0) {
      localStorage.setItem('insights-meeting-disposition-filter', JSON.stringify(dispositions));
    } else {
      localStorage.removeItem('insights-meeting-disposition-filter');
    }
  };

  const handleSalesRepChange = (salesRep: string | null) => {
    setSelectedSalesRep(salesRep);
    if (salesRep) {
      localStorage.setItem('insights-sales-rep-filter', salesRep);
    } else {
      localStorage.removeItem('insights-sales-rep-filter');
    }
  };

  const handleTeamChange = (team: string | null) => {
    setSelectedTeam(team);
    setSelectedSalesRep(null);
    if (team) {
      localStorage.setItem('insights-team-filter', team);
    } else {
      localStorage.removeItem('insights-team-filter');
    }
    localStorage.removeItem('insights-sales-rep-filter');
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 font-plus-jakarta">Sales Insights</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm font-inter mt-1">
            Actionable recommendations to increase win rates and optimize your sales process
          </p>
        </div>

        {/* Filters */}
        <FilterBar
          selectedCycleTypes={selectedCycleTypes}
          selectedAccountValues={selectedAccountValues}
          selectedMeetingDispositions={selectedMeetingDispositions}
          selectedTeam={selectedTeam}
          selectedSalesRep={selectedSalesRep}
          onCycleTypesChange={handleCycleTypesChange}
          onAccountValuesChange={handleAccountValuesChange}
          onMeetingDispositionsChange={handleMeetingDispositionsChange}
          onTeamChange={handleTeamChange}
          onSalesRepChange={handleSalesRepChange}
          isFetching={isFetching}
          collapsible={true}
          defaultCollapsed={true}
        />
      </div>

      {/* Insights Content */}
      <InsightsTab
        selectedCycleTypes={selectedCycleTypes}
        selectedAccountValues={selectedAccountValues}
        selectedSalesRep={selectedSalesRep}
        selectedTeam={selectedTeam}
        hideHeader={true}
        isFetching={isFetching}
        tacticalData={tacticalData}
        strategicData={strategicData}
        isLoading={isLoading}
        hasError={hasError}
      />
    </div>
  );
};

export default Insights;